import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineUpload, HiOutlineCheckCircle } from 'react-icons/hi';
import { API_BASE_URL } from '../services/api';

const SegmentToggle = ({ isNew, onExisting, onNew, disabled }) => (
  <div className="inline-flex rounded-full p-1 gap-1" style={{ background: 'var(--background-subtle)', border: '1px solid var(--card-border)' }}>
    <button
      type="button"
      onClick={onExisting}
      disabled={disabled}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${isNew ? '' : 'button-primary'}`}
      style={isNew ? { color: 'var(--foreground-muted)' } : undefined}
    >
      Existing
    </button>
    <button
      type="button"
      onClick={onNew}
      disabled={disabled}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${isNew ? 'button-primary' : ''}`}
      style={isNew ? undefined : { color: 'var(--foreground-muted)' }}
    >
      New
    </button>
  </div>
);

const Upload = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [categoryKey, setCategoryKey] = useState('uncategorized');
  const [seriesKey, setSeriesKey] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [categories, setCategories] = useState([]);
  const [series, setSeries] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newSeriesName, setNewSeriesName] = useState('');
  const [isNewCategory, setIsNewCategory] = useState(false);
  const [isNewSeries, setIsNewSeries] = useState(false);

  useEffect(() => {
    // Fetch existing categories and series
    const fetchOptions = async () => {
      try {
        const [catResponse, seriesResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/categories`),
          fetch(`${API_BASE_URL}/api/series`)
        ]);

        if (catResponse.ok) {
          const catData = await catResponse.json();
          setCategories(catData.categories || []);
        }

        if (seriesResponse.ok) {
          const seriesData = await seriesResponse.json();
          setSeries(seriesData.series || []);
        }
      } catch (err) {
        console.error('Failed to fetch categories/series:', err);
      }
    };

    fetchOptions();
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.toLowerCase().endsWith('.epub')) {
      setFile(selectedFile);
      setError('');
    } else {
      setFile(null);
      setError('Please select a valid EPUB file');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('Please select an EPUB file to upload');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess(null);

    const formData = new FormData();
    formData.append('file', file);

    // Use new category/series names if creating new ones
    const finalCategoryKey = isNewCategory
      ? newCategoryName.toLowerCase().replace(/\s+/g, '-')
      : categoryKey;

    const finalSeriesKey = isNewSeries && newSeriesName
      ? newSeriesName.toLowerCase().replace(/\s+/g, '-')
      : seriesKey;

    formData.append('category_key', finalCategoryKey);
    if (finalSeriesKey) {
      formData.append('series_key', finalSeriesKey);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/upload-epub`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess({
          title: data.title,
          reading_key: data.reading_key,
          words: data.words
        });

        // Reset form
        setFile(null);
        setCategoryKey('uncategorized');
        setSeriesKey('');
        setNewCategoryName('');
        setNewSeriesName('');
        setIsNewCategory(false);
        setIsNewSeries(false);

        // Clear file input
        const fileInput = document.getElementById('epub-file-input');
        if (fileInput) {
          fileInput.value = '';
        }
      } else {
        setError(data.error || 'Failed to upload EPUB file');
      }
    } catch (err) {
      setError('Network error: Failed to upload file');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--foreground)' }}>
            Upload EPUB Book
          </h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--foreground-muted)' }}>
            Add a new book to your library by uploading an EPUB file.
          </p>
        </div>

        <div className="surface-card rounded-2xl p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* File Input */}
            <div>
              <label htmlFor="epub-file-input" className="block text-sm font-semibold mb-2" style={{ color: 'var(--foreground)' }}>
                EPUB File
              </label>
              <input
                id="epub-file-input"
                type="file"
                accept=".epub"
                onChange={handleFileChange}
                className="field block w-full rounded-lg text-sm px-3 py-2 file:mr-4 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[var(--accent-tint)] file:text-[var(--accent)]"
                disabled={uploading}
              />
              {file && (
                <p className="mt-2 text-sm" style={{ color: 'var(--foreground-muted)' }}>
                  Selected: {file.name}
                </p>
              )}
            </div>

            {/* Category Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
                  Category
                </label>
                <SegmentToggle
                  isNew={isNewCategory}
                  disabled={uploading}
                  onExisting={() => { setIsNewCategory(false); setNewCategoryName(''); }}
                  onNew={() => setIsNewCategory(true)}
                />
              </div>
              {!isNewCategory ? (
                <select
                  value={categoryKey}
                  onChange={(e) => setCategoryKey(e.target.value)}
                  className="field block w-full px-3 py-2 rounded-lg text-sm"
                  disabled={uploading}
                >
                  <option value="uncategorized">Uncategorized</option>
                  {categories.map((cat) => (
                    <option key={cat.key} value={cat.key}>
                      {cat.title}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Enter new category name"
                  className="field block w-full px-3 py-2 rounded-lg text-sm"
                  disabled={uploading}
                />
              )}
            </div>

            {/* Series Selection (Optional) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
                  Series <span className="font-normal" style={{ color: 'var(--foreground-muted)' }}>(Optional)</span>
                </label>
                <SegmentToggle
                  isNew={isNewSeries}
                  disabled={uploading}
                  onExisting={() => { setIsNewSeries(false); setNewSeriesName(''); }}
                  onNew={() => setIsNewSeries(true)}
                />
              </div>
              {!isNewSeries ? (
                <select
                  value={seriesKey}
                  onChange={(e) => setSeriesKey(e.target.value)}
                  className="field block w-full px-3 py-2 rounded-lg text-sm"
                  disabled={uploading}
                >
                  <option value="">No Series</option>
                  {series.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.title}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={newSeriesName}
                  onChange={(e) => setNewSeriesName(e.target.value)}
                  placeholder="Enter new series name"
                  className="field block w-full px-3 py-2 rounded-lg text-sm"
                  disabled={uploading}
                />
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="notice-error rounded-lg p-4 text-sm">{error}</div>
            )}

            {/* Success Message */}
            {success && (
              <div className="notice-success rounded-lg p-4 space-y-1.5">
                <p className="text-sm font-semibold flex items-center gap-2">
                  <HiOutlineCheckCircle className="w-4 h-4" aria-hidden="true" />
                  Book uploaded successfully!
                </p>
                <p className="text-sm">Title: {success.title}</p>
                <p className="text-sm">Words: {success.words.toLocaleString()}</p>
                <button
                  type="button"
                  onClick={() => navigate(`/reading/${success.reading_key}/read`)}
                  className="mt-1 text-sm font-medium underline"
                >
                  Open Book →
                </button>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="button-ghost px-4 py-2 text-sm font-medium rounded-lg"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!file || uploading}
                className="button-primary px-5 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--accent-foreground)', borderTopColor: 'transparent' }} />
                    Uploading...
                  </>
                ) : (
                  <>
                    <HiOutlineUpload className="w-4 h-4" aria-hidden="true" />
                    Upload Book
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Upload;
